// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html"
// Establish Phoenix Socket and LiveView configuration.
import {Socket} from "phoenix"
import {LiveSocket} from "phoenix_live_view"
import topbar from "../vendor/topbar"
import Sentry from "../vendor/sentry"
import Hooks from "./hooks/index"
import {safeStorage} from "./safe-storage"

// Storage that degrades to in-memory when the browser blocks it, so a
// SecurityError can't crash init (#331). Reused by LiveSocket and the theme
// toggle below.
const sessionStore = safeStorage("session")
const localStore = safeStorage("local")

// Error tracking — Sentry-compatible browser SDK (GlitchTip). The server only
// renders the sentry-dsn meta tag when SENTRY_JS_DSN is set, so this is a
// complete no-op in dev/test. Errors only: no tracing, no session replay.
const sentryDsn = document.querySelector("meta[name='sentry-dsn']")?.content
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: document.querySelector("meta[name='sentry-environment']")?.content || "production",
    // Stamp events with the deploy's release so GlitchTip can group them and
    // (later) match uploaded source maps. Mirrors the server SDK's release.
    release: document.querySelector("meta[name='sentry-release']")?.content || undefined,
    tracesSampleRate: 0,
    // Drop noise that isn't our bug to fix: browser-extension chatter and the
    // third-party Jitsu analytics script. These were auto-filing junk GitHub
    // issues (e.g. "runtime.sendMessage() Tab not found", Jitsu "Failed to
    // send JSON") with no actionable signal.
    ignoreErrors: [
      /runtime\.sendMessage/i,
      /Tab not found/i,
      /Failed to send JSON/i,
      /Extension context invalidated/i,
      // Transient LiveView/Phoenix client timeouts: when a channel push or
      // server reply exceeds the socket timeout, the client throws a bare
      // "timeout" and then reconnects on its own, so the user recovers. It's
      // noise, not an actionable bug.
      /^timeout$/i,
      /reading ['"]isConnected['"]/i,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-(web-)?extension:\/\//i,
      // Jitsu analytics loader — third-party, not our app code.
      /\/s\/lib\.js/i,
    ],
  })
}

// Analytics — Jitsu v1 tracker
// Both values are rendered by the server only when their runtime environment
// variables are configured. This keeps staging and review deployments fully
// independent from production analytics.
const jitsuHost = document.querySelector("meta[name='jitsu-host']")?.content?.replace(/\/$/, "")
const jitsuWriteKey = document.querySelector("meta[name='jitsu-write-key']")?.content

if (jitsuHost && jitsuWriteKey) {
  window.jitsuQ = window.jitsuQ || []
  window.jitsu = window.jitsu || (function() { window.jitsuQ.push(arguments) })
  const script = document.createElement("script")
  script.src = `${jitsuHost}/s/lib.js`
  script.setAttribute("data-key", jitsuWriteKey)
  script.setAttribute("data-init-only", "false")
  script.defer = true
  document.head.appendChild(script)
}

// Theme toggle
window.toggleTheme = function() {
  const html = document.documentElement
  const current = html.getAttribute("data-theme")
  const next = current === "courtside-dark" ? "courtside" : "courtside-dark"
  html.setAttribute("data-theme", next)
  localStore.setItem("theme", next)
}

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content")
const liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: {_csrf_token: csrfToken},
  hooks: Hooks,
  // Guarded storage so a blocked sessionStorage can't throw during init (#331).
  sessionStorage: sessionStore,
  localStorage: localStore,
})

// Show progress bar on live navigation and form submits
topbar.config({barColors: {0: "#29d"}, shadowColor: "rgba(0, 0, 0, .3)"})
window.addEventListener("phx:page-loading-start", _info => topbar.show(300))
window.addEventListener("phx:page-loading-stop", _info => topbar.hide())

// connect if there are any LiveViews on the page
liveSocket.connect()

// Global connection-state handling — one place, applies to every LiveView.
//
// When the socket drops (deploy, server restart, network blip) we immediately
// mark <body> so write controls are paused. The warning itself is tiered:
// routine blips stay invisible for four seconds, then get a compact reconnect
// pill, and only a sustained 15-second interruption escalates its wording and
// color. Reconnecting clears every state immediately.
//
// We don't gate the initial pre-connect render: LiveView doesn't bind click
// handlers or mount hooks until it connects, so nothing can fire there.
const CONNECTION_GRACE_MS = 4000
const CONNECTION_ESCALATION_MS = 15000
const connectionBanner = document.getElementById("connection-banner")
const connectionMessage = document.getElementById("connection-banner-message")

let connectionDown = false
let connectionGraceTimer
let connectionEscalationTimer

const clearConnectionTimers = () => {
  clearTimeout(connectionGraceTimer)
  clearTimeout(connectionEscalationTimer)
  connectionGraceTimer = undefined
  connectionEscalationTimer = undefined
}

const showConnectionBanner = (state, message) => {
  if (!connectionBanner || !connectionMessage) return

  connectionBanner.dataset.state = state
  connectionMessage.textContent = message
  connectionBanner.hidden = false
}

const markConnected = () => {
  connectionDown = false
  clearConnectionTimers()
  document.body.classList.remove("lv-disconnected")
  delete document.body.dataset.connectionState

  if (connectionBanner) {
    connectionBanner.hidden = true
    delete connectionBanner.dataset.state
  }
}

const markDisconnected = () => {
  // onError and onClose commonly fire for the same interruption. Starting a
  // second timer sequence would make the warning appear after recovery.
  if (connectionDown) return

  connectionDown = true
  document.body.classList.add("lv-disconnected")
  document.body.dataset.connectionState = "grace"

  connectionGraceTimer = setTimeout(() => {
    if (!connectionDown) return

    document.body.dataset.connectionState = "reconnecting"
    showConnectionBanner("reconnecting", "Reconnecting… changes are paused.")
  }, CONNECTION_GRACE_MS)

  connectionEscalationTimer = setTimeout(() => {
    if (!connectionDown) return

    document.body.dataset.connectionState = "interrupted"
    showConnectionBanner(
      "interrupted",
      "Connection interrupted — changes remain paused."
    )
  }, CONNECTION_ESCALATION_MS)
}

liveSocket.socket.onOpen(markConnected)
liveSocket.socket.onClose(markDisconnected)
liveSocket.socket.onError(markDisconnected)

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (process.env.NODE_ENV === "development") {
  window.addEventListener("phx:live_reload:attached", ({detail: reloader}) => {
    // Enable server log streaming to client.
    // Disable with reloader.disableServerLogs()
    reloader.enableServerLogs()

    // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
    //
    //   * click with "c" key pressed to open at caller location
    //   * click with "d" key pressed to open at function component definition location
    let keyDown
    window.addEventListener("keydown", e => keyDown = e.key)
    window.addEventListener("keyup", _e => keyDown = null)
    window.addEventListener("click", e => {
      if(keyDown === "c"){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtCaller(e.target)
      } else if(keyDown === "d"){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtDef(e.target)
      }
    }, true)

    window.liveReloader = reloader
  })
}
